import React, { useState, useRef, useEffect } from 'react';
import {
  DialogContent,
  Typography,
  Box,
  Fade,
  CircularProgress,
  Alert,
  Slide,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { Close, CloudUpload, AttachFile, CheckCircle } from '@mui/icons-material';
import {
  PrimaryButton,
  StyledDialog,
  DialogHeaderStyled,
  StyledCloseButton,
  CancelButton,
} from '../../../utils/common/styles/commonStyles';
import { useDispatch, useSelector } from 'react-redux';
import { uploadPending, uploadError } from '../store/mile.selectors';
import { uploadFile } from '../store/mile.actions';
import PropTypes from 'prop-types';

// Animations
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Transition component for modal
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Styled Components
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(4),
  position: 'relative',
}));

const FormContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

const UploadBox = styled(Box)(({ theme, hasFile }) => ({
  border: `2px dashed ${hasFile ? theme.palette.success.main : '#cbd5e1'}`,
  borderRadius: '12px',
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: hasFile ? '#f0fdf4' : '#f8fafc',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#f0f9ff',
  },
}));

const FileInfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: '#f1f5f9',
  borderRadius: '8px',
  marginTop: theme.spacing(2),
}));

const MileMarkerUpload = ({ open, setOpen, onUploadSuccess }) => {
  const dispatch = useDispatch();
  const loading = useSelector(uploadPending);
  const error = useSelector(uploadError);

  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [showAlert, setShowAlert] = useState({
    open: false,
    message: '',
    severity: '',
  });

  const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB

  useEffect(() => {
    if (!loading && error !== null) {
      setShowAlert({
        open: true,
        message: error,
        severity: 'error',
      });
    }
  }, [error, loading]);

  const validateFile = (file) => {
    if (!file) {
      setValidationError('A file is required');
      return false;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setValidationError('File size must be less than 500MB');
      return false;
    }

    // Check file type
    const isCSV =
      file.type === 'text/csv' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.toLowerCase().endsWith('.csv');

    if (!isCSV) {
      setValidationError('Only CSV files are supported');
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (validateFile(file)) {
        setSelectedFile(file);
      } else {
        setSelectedFile(null);
        event.target.value = '';
      }
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setValidationError('Please select a file');
      return;
    }

    if (!validateFile(selectedFile)) {
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    const result = await dispatch(uploadFile(formData));

    if (result.status === 'success') {
      setShowAlert({
        open: true,
        message: result.message,
        severity: 'success',
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
        setSelectedFile(null);
      }

      // Notify parent component to refresh history
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setValidationError('');
    setShowAlert({ open: false, message: '', severity: '' });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setOpen(false);
  };

  const handleAlertClose = (reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowAlert((prev) => ({ ...prev, open: false }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      slots={{ transition: Transition }}
      keepMounted
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: 480,
          width: '100%',
          animation: `${slideUp} 0.4s ease-out`,
          margin: '16px',
        },
      }}
    >
      <DialogHeaderStyled
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <CloudUpload />
        Upload File
        <StyledCloseButton onClick={handleClose} size="small">
          <Close />
        </StyledCloseButton>
      </DialogHeaderStyled>

      <StyledDialogContent>
        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            marginBottom: 3,
            fontWeight: 500,
          }}
        >
          Upload your CSV file. Max size: 500MB
        </Typography>

        <FormContainer>
          <input
            type="file"
            ref={fileInputRef}
            id="file-input"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept=".csv,text/csv"
          />

          <label htmlFor="file-input">
            <UploadBox hasFile={selectedFile}>
              {selectedFile ? (
                <CheckCircle sx={{ fontSize: 48, color: '#22c55e', marginBottom: 2 }} />
              ) : (
                <CloudUpload sx={{ fontSize: 48, color: '#94a3b8', marginBottom: 2 }} />
              )}
              <Typography variant="h6" sx={{ color: '#334155', fontWeight: 600, marginBottom: 1 }}>
                {selectedFile ? 'File Selected' : 'Click to browse'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                {selectedFile ? 'Click to change file' : 'Select a CSV file to upload'}
              </Typography>
            </UploadBox>
          </label>

          {selectedFile && (
            <FileInfoBox>
              <AttachFile sx={{ color: '#3b82f6' }} />
              <Box flex={1}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
            </FileInfoBox>
          )}

          {validationError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {validationError}
            </Alert>
          )}

          <Fade in={showAlert.open} timeout={300}>
            <Box>
              {showAlert.open && (
                <Alert
                  severity={showAlert.severity}
                  sx={{
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                      fontWeight: 500,
                    },
                  }}
                  onClose={handleAlertClose}
                >
                  {showAlert.message}
                </Alert>
              )}
            </Box>
          </Fade>

          <Box
            display="flex"
            gap={2}
            justifyContent="center"
            flexDirection={{ xs: 'column', sm: 'row' }}
          >
            <CancelButton onClick={handleClose} sx={{ order: { xs: 2, sm: 1 } }}>
              Cancel
            </CancelButton>

            <PrimaryButton
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !selectedFile}
              sx={{ order: { xs: 1, sm: 2 } }}
            >
              {loading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} color="inherit" />
                  <span>Uploading...</span>
                </Box>
              ) : (
                'Upload'
              )}
            </PrimaryButton>
          </Box>
        </FormContainer>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default MileMarkerUpload;

MileMarkerUpload.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  onUploadSuccess: PropTypes.func,
};
